import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSSO, msalApplicationID, msalTenantID, msalScope, logo } from 'config';
import { get, set } from 'lodash';
import { InteractionStatus, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider, useMsalAuthentication, useMsal, AuthenticatedTemplate, useIsAuthenticated } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from 'material-ui';
import pallet from 'constants/colors';

import App from './App';
import { injectQDataInterceptor } from '../utils/Api';
import { sso } from '../actions/account/login';
import KButton from '../components/KJRN/KButton';
import KTitle from '../components/KJRN/KTitle';
import PanelMessage from '../components/panels/PanelMessage';

const styles = {
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    paddingLeft: 12,
    paddingRight: 12,
    backgroundColor: 'white',
    height: '100vh',
  },
  logo: {
    maxHeight: 150,
    maxWidth: 220,
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: 800,
    marginTop: '6%',
    width: '100%',
  },
  art1: {
    bottom: 0,
    right: 0,
    position: 'absolute',
    width: 0,
    height: 0,
    borderBottom: '40vw solid rgb(35, 95, 167)',
    borderLeft: '55vw solid transparent',
    opacity: 0.3,
    borderBottomColor: pallet.loginColor,
  },
  art2: {
    bottom: 0,
    right: 0,
    position: 'absolute',
    width: 0,
    height: 0,
    borderBottom: '25vw solid rgb(35, 95, 167)',
    borderLeft: '60vw solid transparent',
    opacity: 0.4,
    borderBottomColor: pallet.loginColor,
  },
  art3: {
    bottom: 0,
    right: 0,
    position: 'absolute',
    width: 0,
    height: 0,
    borderBottom: '15vw solid rgb(35, 95, 167)',
    borderLeft: '100vw solid transparent',
    opacity: 0.7,
    borderBottomColor: pallet.loginColor,
  },
  error: {
    padding: 12,
    color: '#d52212',
    whiteSpace: 'pre-wrap',
  },
};

const MSALAuth = () => {
  const request = {
    scopes: [`api://${msalApplicationID}/${msalScope}`],
  };
  const { login, error } = useMsalAuthentication(InteractionType.Redirect, request);
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const loading = inProgress !== InteractionStatus.None;

  if (isAuthenticated) {
    return null;
  }

  return (
    <div style={styles.container}>
      <div style={styles.logoContainer}>
        <img style={styles.logo} src={logo} alt="logo" />
      </div>
      <div style={{ width: 320, marginTop: '10em', padding: 16 }}>
        <KTitle title="Innskráning" style={{ marginBottom: 32 }} />
        <KButton
          disabled={loading}
          onClick={login}
          label="Innskrá með Microsoft"
        />
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%',
          }}
        >
          {loading ? <CircularProgress /> : null}
          {error ? (
            <div style={styles.error}>
              {error.errorMessage || error.message}
            </div>
          ) : null}
        </div>
      </div>
      <div style={styles.art1} />
      <div style={styles.art2} />
      <div style={styles.art3} />
    </div>
  );
};

const UserWrapper = ({ children }) => {
  const dispatch = useDispatch();
  const { accounts, instance, inProgress } = useMsal();
  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      dispatch(sso());
    }
  }, [dispatch, sso]);
  const username = get(accounts, [0, 'username']);
  const isAuthenticated = useSelector(state => get(state, 'loginReducer.isAuthenticated'));
  const error = useSelector(state => get(state, 'loginReducer.error'));

  if (error) {
    return (
      <div
        style={{
            width: '100%', height: '50vh', display: 'flex', justifyContent: 'center',
        }}
      >
        <div
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <PanelMessage
            iconName="error"
            type="error"
            message={(
              <span style={{ marginRight: 12 }}>
                Error authenticating
                <br />
                {error.myUserInfo ? `Kjarna user not configured for email: ${username}` : ''}
              </span>
            )}
            action={() => instance.logout()}
            actionLabel="Logout"
          />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div
        style={{
            width: '100%', height: '50vh', display: 'flex', justifyContent: 'center',
        }}
      >
        <div
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <CircularProgress size={70} />
          <span style={{ fontSize: 18, fontWeight: 600, marginTop: 12 }}>Loading...</span>
        </div>
      </div>
    );
  }

  return children;
};

let pca;

if (useSSO) {
  const configuration = {
    auth: {
      clientId: msalApplicationID,
      authority: `https://login.microsoftonline.com/${msalTenantID}`,
      redirectUri: `${window.location.origin}`,
    },
  };
  pca = new PublicClientApplication(configuration);
  injectQDataInterceptor(async (config) => {
    set(config, 'headers.Content-Type', 'application/json');
    set(config, 'headers.Accept', 'application/json');
    const account = get(pca.getAllAccounts(), 0);
    if (!account) return config;

    const request = {
      scopes: [`api://${msalApplicationID}/${msalScope}`],
      account,
    };

    let accessToken = '';
    try {
      const response = await pca.acquireTokenSilent(request);
      accessToken = get(response, 'accessToken');
    } catch (e) {
      const response = await pca.acquireTokenRedirect(request);
      accessToken = get(response, 'accessToken');
    }
    const authorization = accessToken ? `Bearer ${accessToken}` : undefined;
    set(config, 'headers.Authorization', authorization);
    return config;
  });
}

const SSOApp = props => (
  <MsalProvider instance={pca}>
    <MSALAuth />
    <AuthenticatedTemplate>
      <UserWrapper>
        <App {...props} />
      </UserWrapper>
    </AuthenticatedTemplate>
  </MsalProvider>
);

UserWrapper.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node),
};

export default SSOApp;



// WEBPACK FOOTER //
// ./src/containers/SSOApp.js